// AI-assisted: Claude 2026-08-08, human-reviewed: ausstehend
// Ein Formular für drei Fälle (Thors Wunsch Runde 12):
//  - neue eigene Übung anlegen (alle Felder)
//  - eigene Übung ändern (alle Felder)
//  - Katalog-Übung: persönlicher Zettel — Video + Notiz, nur für mich
// Der gemeinsame Katalog bleibt unangetastet; wer das Katalog-Recht hat
// (Admin oder may_curate), darf zusätzlich ein Video „für alle" hinterlegen.
import { useState } from "react"
import type { Exercise } from "@/lib/types"
import { BODY_PART_DE, EQUIPMENT_DE } from "@/lib/labels"
import { createExercise, istEigene, saveNote, setKatalogVideo, updateExercise } from "@/lib/uebungen"
import { darfKuratieren } from "@/lib/mitglieder"
import { sichereUrl } from "@/lib/video"
import { toast } from "@/lib/toast"

const OFFLINE_MSG = "Dafür brauchst du Internet – bitte später erneut versuchen"

// Häufigste zuerst, Rest alphabetisch — die Werte selbst sind Datenset-Vokabular
const PARTIEN = Object.entries(BODY_PART_DE).sort((a, b) => a[1].localeCompare(b[1], "de"))
const GERAETE = Object.entries(EQUIPMENT_DE).sort((a, b) => a[1].localeCompare(b[1], "de"))

export default function UebungForm({ exercise, notiz, onFertig, onAbbruch }: {
  /** undefined = neue Übung anlegen */
  exercise?: Exercise
  /** aktueller Zettel-Text bei Katalog-Übungen */
  notiz?: { video_url: string; note: string }
  onFertig: (ex?: Exercise) => void
  onAbbruch: () => void
}) {
  const neu = !exercise
  const eigene = neu || (exercise ? istEigene(exercise) : false)

  const [name, setName] = useState(exercise?.name ?? "")
  const [part, setPart] = useState(exercise?.body_part ?? "")
  const [equip, setEquip] = useState(exercise?.equipment ?? "body weight")
  const [text, setText] = useState(eigene ? (exercise?.execution_text ?? "") : (notiz?.note ?? ""))
  const [url, setUrl] = useState(eigene ? (exercise?.video_url ?? "") : (notiz?.video_url ?? ""))
  const [saetze, setSaetze] = useState(String(exercise?.default_sets || 3))
  const [wdh, setWdh] = useState(exercise?.default_reps ?? "10–12")
  const [fuerAlle, setFuerAlle] = useState(false)
  const [busy, setBusy] = useState(false)

  const urlOk = !url.trim() || sichereUrl(url) !== null
  const kannSpeichern = (eigene ? name.trim().length > 1 : true) && urlOk && !busy

  async function speichern() {
    if (!kannSpeichern) return
    setBusy(true)
    try {
      if (neu) {
        const ex = await createExercise({
          name, body_part: part, equipment: equip, execution_text: text,
          video_url: url, default_sets: Number(saetze) || 3, default_reps: wdh,
        })
        toast(`${ex.name} angelegt ✓`)
        onFertig(ex)
      } else if (eigene && exercise) {
        await updateExercise(exercise.id, {
          name, body_part: part, equipment: equip, execution_text: text,
          video_url: url, default_sets: Number(saetze) || 3, default_reps: wdh,
        })
        toast("Übung gespeichert ✓")
        onFertig()
      } else if (exercise) {
        await saveNote(exercise.id, url, text)
        if (fuerAlle && darfKuratieren()) await setKatalogVideo(exercise.id, url)
        toast(fuerAlle ? "Für alle hinterlegt ✓" : "Für dich gespeichert ✓")
        onFertig()
      }
    } catch {
      toast(OFFLINE_MSG)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card uebform">
      <h3 className="uebform-titel">
        {neu ? "Eigene Übung anlegen" : eigene ? "Übung bearbeiten" : `Deine Ergänzung zu „${exercise?.name}"`}
      </h3>
      {!eigene && (
        <p className="hint" style={{ margin: "0 0 10px" }}>
          Diese Übung gehört zum gemeinsamen Katalog. Dein Video und deine Notiz sieht nur du.
        </p>
      )}

      {eigene && (
        <>
          <label htmlFor="ue-name">Name der Übung</label>
          <input id="ue-name" type="text" maxLength={120} value={name} placeholder="z. B. Nackenrolle am Seil"
            onChange={(e) => setName(e.target.value)} autoFocus={neu} />

          <label htmlFor="ue-part">Körperbereich</label>
          <select id="ue-part" value={part} onChange={(e) => setPart(e.target.value)}>
            <option value="">– keine Angabe –</option>
            {PARTIEN.map(([wert, label]) => <option key={wert} value={wert}>{label}</option>)}
          </select>

          <label htmlFor="ue-equip">Gerät</label>
          <select id="ue-equip" value={equip} onChange={(e) => setEquip(e.target.value)}>
            {GERAETE.map(([wert, label]) => <option key={wert} value={wert}>{label}</option>)}
          </select>

          <div className="uebform-zwei">
            <div>
              <label htmlFor="ue-sets">Sätze</label>
              <input id="ue-sets" type="number" min={1} max={20} inputMode="numeric"
                value={saetze} onChange={(e) => setSaetze(e.target.value)} />
            </div>
            <div>
              <label htmlFor="ue-reps">Wiederholungen</label>
              <input id="ue-reps" type="text" maxLength={50} value={wdh} placeholder="z. B. 10–12 oder 30 Sek."
                onChange={(e) => setWdh(e.target.value)} />
            </div>
          </div>
        </>
      )}

      <label htmlFor="ue-text">{eigene ? "Ausführung" : "Deine Notiz"}</label>
      <textarea id="ue-text" rows={3} value={text} maxLength={2000}
        placeholder={eigene ? "Worauf kommt es an?" : "z. B. Sitzhöhe 4, Griff eng"}
        onChange={(e) => setText(e.target.value)} />

      <label htmlFor="ue-url">Video-Link</label>
      <input id="ue-url" type="url" inputMode="url" value={url} placeholder="https://www.youtube.com/watch?v=…"
        onChange={(e) => setUrl(e.target.value)} />
      {!urlOk && <p className="form-error" role="alert">Das ist keine Web-Adresse. Sie muss mit https:// beginnen.</p>}
      <p className="hint" style={{ margin: "6px 0 0" }}>
        Ohne Link sucht die App weiterhin selbst auf YouTube nach der Übung.
      </p>

      {!eigene && darfKuratieren() && (
        <label className="opt-check" style={{ marginTop: 12 }}>
          <input type="checkbox" checked={fuerAlle} onChange={(e) => setFuerAlle(e.target.checked)} />
          Video für alle hinterlegen – jedes Mitglied sieht es dann
        </label>
      )}

      <div className="uebform-actions">
        <button type="button" className="btn-secondary pressable" onClick={onAbbruch}>Abbrechen</button>
        <button type="button" className="save pressable" style={{ marginTop: 0 }} disabled={!kannSpeichern} onClick={() => void speichern()}>
          {busy ? "Speichert…" : "Speichern"}
        </button>
      </div>
    </div>
  )
}
